/* eslint-disable react/no-array-index-key */
import {Box, Text, Progress} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {Decimal} from 'decimal.js'
import {Drawer} from '@/shared/components/ui/drawer'
import {Suspense, useState} from 'react'
import {Table} from '@chakra-ui/react'
import {AdministrationHistoryDrawer} from '../drawers/administration-history-drawer'
import {LuCircleAlert} from 'react-icons/lu'
import {DrugDetails} from '@cypro/engine/utils'
import {CatalogType} from '@cypro/engine/database/schema'
import {numberFormatter} from '@cypro/ui/utils'

interface Props {
  cumulatedDoses: DrugDetails[]
}

export function CumulatedDoseTable({cumulatedDoses}: Props) {
  const {t} = useTranslate('common')
  const [selectedDose, setSelectedDose] = useState<DrugDetails | null>(null)
  return (
    <>
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>{t('therapy_sequence.table.medication')}</Table.ColumnHeader>
            <Table.ColumnHeader>{t('max_dose_per_cycle')}</Table.ColumnHeader>
            <Table.ColumnHeader>{t('cumulated_dose')}</Table.ColumnHeader>
            <Table.ColumnHeader>{t('max_lifetime_dose')}</Table.ColumnHeader>
            <Table.ColumnHeader>{t('remaining')}</Table.ColumnHeader>
            <Table.ColumnHeader>{t('usage')}</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {cumulatedDoses.map((dose, index) => {
            if (dose.drugType !== CatalogType.enum.ACTIVE_SUBSTANCE) return null
            const lifetimeLimit = dose.drugLimits?.lifetime ? new Decimal(dose.drugLimits?.lifetime) : undefined
            const amountToToday = dose.totalAmount ? new Decimal(dose.totalAmount) : undefined

            const percentage =
              lifetimeLimit && amountToToday ? (100 * amountToToday.toNumber()) / lifetimeLimit.toNumber() : 0
            const remaining = lifetimeLimit && amountToToday ? lifetimeLimit.minus(amountToToday) : undefined
            return (
              <Table.Row key={index} onClick={() => setSelectedDose(dose)} cursor="pointer" _hover={{bg: 'bg.muted'}}>
                <Table.Cell border="none">{dose.drugName || 'Unknown'}</Table.Cell>
                <Table.Cell border="none">
                  <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                    <Text fontWeight={'normal'} fontSize={'sm'} color={'fg.muted'}>
                      {dose.drugLimits?.perCycle ? numberFormatter.format(dose.drugLimits.perCycle) : '-'}
                    </Text>
                    <Text fontWeight={'normal'} fontSize={'sm'} color={'fg.muted'}>
                      {dose.doseUnit || '-'}
                    </Text>
                  </Box>
                </Table.Cell>
                <Table.Cell border="none">
                  <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                    <Text fontWeight={'normal'} fontSize={'sm'} color={'fg.muted'}>
                      {amountToToday && amountToToday.greaterThan(0)
                        ? numberFormatter.format(amountToToday.toNumber())
                        : '-'}
                    </Text>

                    <Text fontWeight={'normal'} fontSize={'sm'} color={'fg.muted'}>
                      {dose.doseUnit || '-'}
                    </Text>
                  </Box>
                </Table.Cell>
                <Table.Cell border="none">
                  <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                    <Text fontWeight={'normal'} fontSize={'sm'} color={'fg.muted'}>
                      {lifetimeLimit !== undefined ? numberFormatter.format(lifetimeLimit.toNumber()) : '-'}
                    </Text>

                    <Text fontWeight={'normal'} fontSize={'sm'} color={'fg.muted'}>
                      {dose.doseUnit || '-'}
                    </Text>
                  </Box>
                </Table.Cell>
                <Table.Cell border="none">
                  <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                    <Text fontWeight="normal" fontSize="sm" color="fg.muted">
                      {remaining !== undefined ? numberFormatter.format(remaining.toNumber()) : '-'}
                    </Text>
                    <Text fontWeight="normal" fontSize="sm" color="fg.muted">
                      {dose.doseUnit || '-'}
                    </Text>
                  </Box>
                </Table.Cell>
                <Table.Cell border="none">
                  {dose.drugLimits?.lifetime ? (
                    <Progress.Root
                      size={'sm'}
                      shape={'rounded'}
                      colorPalette={percentage > 50 ? 'orange' : 'green'}
                      borderRadius={'full'}
                      value={percentage > 100 ? 100 : percentage}
                      max={100}
                      width={'100%'}
                    >
                      <Progress.Label mb="2">
                        {t('percentage_reached', {percentage: percentage.toFixed(2)})}
                      </Progress.Label>
                      <Progress.Track borderRadius={'full'}>
                        <Progress.Range />
                      </Progress.Track>
                    </Progress.Root>
                  ) : (
                    <Box display={'flex'} alignItems={'center'} gap={2}>
                      <LuCircleAlert size={16} />
                      <Text fontWeight={'normal'} fontSize={'sm'} color={'fg.muted'}>
                        {t('lifetime_limit_missing')}
                      </Text>
                    </Box>
                  )}
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>
      <Drawer open={Boolean(selectedDose)} onOpenChange={changes => !changes.open && setSelectedDose(null)}>
        <Suspense fallback={null}>{selectedDose && <AdministrationHistoryDrawer dose={selectedDose} />}</Suspense>
      </Drawer>
    </>
  )
}
